import { usePermissions } from "./PermissionProvider";
import { RoleName, ModuleName } from "./types";

/**
 * Custom hook to check permissions
 * Provides easy-to-use permission checking throughout the app
 */
export const usePermission = () => {
  const {
    user,
    userRole,
    userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isFullAccess,
  } = usePermissions();

  /**
   * Check if user can access a specific module
   */
  const canAccessModule = (
    module: ModuleName,
    requiredPermissions?: string[],
  ): boolean => {
    // Full access users can access everything
    if (isFullAccess) return true;

    // If no specific permissions required, allow access
    if (!requiredPermissions || requiredPermissions.length === 0) return true;

    // Check if user has at least one of the required permissions
    return hasAnyPermission(requiredPermissions);
  };

  /**
   * Check if user can perform a specific action
   */
  const canPerformAction = (
    requiredPermissions: string | string[],
  ): boolean => {
    return hasPermission(requiredPermissions);
  };

  /**
   * Check if user can access a route
   */
  const canAccessRoute = (requiredPermissions?: string[]): boolean => {
    if (isFullAccess) return true;
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return hasAnyPermission(requiredPermissions);
  };

  /**
   * Check if user can see a menu item
   */
  const canSeeMenuItem = (requiredPermissions?: string[]): boolean => {
    if (isFullAccess) return true;
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return hasAnyPermission(requiredPermissions);
  };

  /**
   * Check if user is a specific role
   */
  const isRole = (role: RoleName): boolean => {
    return hasRole(role);
  };

  /**
   * Check if user is one of the specified roles
   */
  const isAnyRole = (roles: RoleName[]): boolean => {
    return hasRole(roles);
  };

  return {
    // User data
    user,
    userRole,
    userPermissions,
    isFullAccess,

    // Permission checkers
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,

    // Convenience methods
    canAccessModule,
    canPerformAction,
    canAccessRoute,
    canSeeMenuItem,
    isRole,
    isAnyRole,
  };
};
